import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import styled from 'styled-components';

// constant
import { SideNavType } from '../../constants/SideNavItems.constant';
// theme
import aampeTheme from '../../constants/aampeTheme';

import { useNavContext } from '../../context/NavContext';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

import useFormulaSequencesStatusCountQuery from '../../hooks/formula-sequence/useFormulaSequencesStatusCountQuery';
// hooks
import useFormulasCategoryCountQuery from '../../hooks/formulas/useFormulasCategoryCount';
import { useAuthSession } from '../../hooks/useAuthSession';
import useEventsQuery from 'hooks/events/useEventsQuery';
import useIsAampeAdmin from 'hooks/helper/useIsAampeAdmin';

import LogoOnly from '../../images/logos/logoOnly.svg?react';

// components
import SideNavButton from './SideNavButton';
import SideNavFooter from './SideNavFooter';
import CollapseToggle from './collapseToggle';
// helper
import { mapSideNavItems } from './helper';

interface CollapsedProps {
    collapsed: boolean;
}
const shouldForwardProps = createShouldForwardProp(['collapsed']);

const SideNav: React.FC<React.PropsWithChildren<unknown>> = () => {
    const { user: currentUser } = useAuthSession();
    const { isCollapsed, setIsCollapsed, forceCollapsed } = useNavContext();

    const collapsed = forceCollapsed || isCollapsed;

    const { data: formulasCount } = useFormulasCategoryCountQuery();
    const { data: formulasSequencesCount } =
        useFormulaSequencesStatusCountQuery();
    const { data: eventsList } = useEventsQuery(false);

    const uncategorisedEventsCount = useMemo(() => {
        return eventsList?.filter((event) => !event.context)?.length;
    }, [eventsList]);

    const isInternalAampeAdmin = useIsAampeAdmin();

    const mappedSideNavItems: SideNavType[] = useMemo(
        () =>
            mapSideNavItems(
                formulasCount,
                formulasSequencesCount,
                isInternalAampeAdmin,
                currentUser?.customerId,
                uncategorisedEventsCount,
            ),
        [
            formulasCount,
            formulasSequencesCount,
            isInternalAampeAdmin,
            currentUser?.customerId,
            uncategorisedEventsCount,
        ],
    );
    const toggleCollapse = () => setIsCollapsed(!isCollapsed);

    if (!currentUser?.name) return null;

    return (
        <Container collapsed={collapsed} className="side-nav">
            <SideNavBody>
                <LogoAndCollapse>
                    <NavLink to={'/dashboard'}>
                        {<LogoOnly width={22} height={30} />}
                    </NavLink>
                </LogoAndCollapse>

                <NavItemsContainer collapsed={collapsed}>
                    {mappedSideNavItems.map((item) => (
                        <SideNavButton
                            key={item.item.text}
                            item={item.item}
                            subItems={item.subItems}
                            collapsed={collapsed}
                        />
                    ))}
                </NavItemsContainer>
            </SideNavBody>
            <FooterContainer collapsed={collapsed}>
                <SideNavFooter collapsed={collapsed} />
            </FooterContainer>
            {!forceCollapsed && (
                <CollapseToggle
                    collapsed={collapsed}
                    onToggle={toggleCollapse}
                />
            )}
        </Container>
    );
};

const Container = styled('div').withConfig({
    shouldForwardProp: shouldForwardProps,
})<CollapsedProps>`
    display: flex;
    flex-flow: column nowrap;
    width: ${(props: CollapsedProps) => (props.collapsed ? '70px' : '228px')};
    justify-content: space-between;
    align-items: flex-start;
    background-color: ${aampeTheme.colors.white};
    flex-shrink: 0;
    transition: all 300ms ease-in-out;
    overflow-x: visible;
    position: relative;

    & .collapse-button {
        display: ${(props: CollapsedProps) =>
            props.collapsed ? 'none' : 'block'};
        background-color: ${aampeTheme.colors.white};
        overflow: visible;
        border: none;
        cursor: pointer;
        padding: ${aampeTheme.spacing4(1)};
        height: calc(16px + ${aampeTheme.spacing4(2)});
    }

    &:hover .collapse-button {
        display: block;
    }
`;

const SideNavBody = styled('div')`
    width: 100%;
    position: absolute;
    height: 100%;
`;
const LogoAndCollapse = styled('div')`
    display: flex;
    align-items: center;
    overflow: visible;
    justify-content: space-between;
    align-content: center;
    gap: ${aampeTheme.spacing(3)};
    margin: ${aampeTheme.spacing(4, 1, 2, 3)};
`;

const NavItemsContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<CollapsedProps>(({ collapsed }) => ({
    overflowY: collapsed ? 'visible' : 'auto',
    position: collapsed ? 'static' : 'absolute',
    top: '5em',
    bottom: collapsed ? '100px' : '210px',
    right: 0,
    left: 0,
    '&::-webkit-scrollbar': {
        display: 'none',
        background: 'transparent',
        width: 0,
    },
}));

const FooterContainer = styled('div').withConfig({
    shouldForwardProp: shouldForwardProps,
})<CollapsedProps>`
    width: 100%;
    padding: ${(props: CollapsedProps) =>
        props.collapsed ? aampeTheme.spacing(1) : aampeTheme.spacing4(4, 6)};
    background-color: ${aampeTheme.colors.white};
    width: 100%;
    display: flex;
    flex-flow: column nowrap;
    position: absolute;
    bottom: 0px;
`;

export default SideNav;
